import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Transportistas from './pages/Transportistas';
import Dispatches from './pages/Dispatches';
import Assignment from './pages/Assignment';
import AuditLogs from './pages/AuditLogs';
import Profile from './pages/Profile';
import Importadoras from './pages/Importadoras';
import Clients from './pages/Clients';
import Businesses from './pages/Businesses';
import DriverView from './pages/DriverView';
import Liquidaciones from './pages/Liquidaciones';
import Proveedores from './pages/Proveedores';
import Inventario from './pages/Inventario';
import Pagos from './pages/Pagos';
import CuentasPorCobrar from './pages/CuentasPorCobrar';
import { FinanzasDashboardPage, FinanzasEntidadesPage, FinanzasCuentasPage, FinanzasMovimientosPage, FinanzasConfiguracionPage, FinanzasResumenPage } from './pages/finanzas';
import ChangePasswordModal from './components/ChangePasswordModal';
import DevRoleSwitcher from './components/DevRoleSwitcher';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setMustChangePassword(response.data.must_change_password);
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData, must_change_password } = response.data;
      setToken(access_token);
      setUser(userData);
      setMustChangePassword(must_change_password);
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      return { success: true, must_change_password };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Error al iniciar sesión' };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  };

  const changePassword = async (oldPassword, newPassword) => {
    try {
      await axios.post(`${API}/auth/change-password`, {
        old_password: oldPassword,
        new_password: newPassword
      });
      setMustChangePassword(false);
      await fetchCurrentUser();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Error al cambiar contraseña' };
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, token, login, logout, loading, changePassword, mustChangePassword, setMustChangePassword }}>
      {children}
    </AuthContext.Provider>
  );
};

const PrivateRoute = ({ children }) => {
  const { user, loading, mustChangePassword } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (mustChangePassword) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <ChangePasswordModal isOpen={true} onClose={() => {}} />
      </div>
    );
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
          <Route path="/users" element={<PrivateRoute><Users /></PrivateRoute>} />
          <Route path="/transportistas" element={<PrivateRoute><Transportistas /></PrivateRoute>} />
          <Route path="/dispatches" element={<PrivateRoute><Dispatches /></PrivateRoute>} />
          <Route path="/assignment" element={<PrivateRoute><Assignment /></PrivateRoute>} />
          <Route path="/importadoras" element={<PrivateRoute><Importadoras /></PrivateRoute>} />
          <Route path="/clients" element={<PrivateRoute><Clients /></PrivateRoute>} />
          <Route path="/businesses" element={<PrivateRoute><Businesses /></PrivateRoute>} />
          <Route path="/driver-view" element={<PrivateRoute><DriverView /></PrivateRoute>} />
          <Route path="/liquidaciones" element={<PrivateRoute><Liquidaciones /></PrivateRoute>} />
          <Route path="/pagos" element={<PrivateRoute><Pagos /></PrivateRoute>} />
          <Route path="/proveedores" element={<PrivateRoute><Proveedores /></PrivateRoute>} />
          <Route path="/inventario" element={<PrivateRoute><Inventario /></PrivateRoute>} />
          <Route path="/cuentas-por-cobrar" element={<PrivateRoute><CuentasPorCobrar /></PrivateRoute>} />
          <Route path="/audit-logs" element={<PrivateRoute><AuditLogs /></PrivateRoute>} />
          <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
          <Route path="/finanzas" element={<PrivateRoute><FinanzasDashboardPage /></PrivateRoute>} />
          <Route path="/finanzas/entidades" element={<PrivateRoute><FinanzasEntidadesPage /></PrivateRoute>} />
          <Route path="/finanzas/cuentas" element={<PrivateRoute><FinanzasCuentasPage /></PrivateRoute>} />
          <Route path="/finanzas/movimientos" element={<PrivateRoute><FinanzasMovimientosPage /></PrivateRoute>} />
          <Route path="/finanzas/resumen" element={<PrivateRoute><FinanzasResumenPage /></PrivateRoute>} />
          <Route path="/finanzas/configuracion" element={<PrivateRoute><FinanzasConfiguracionPage /></PrivateRoute>} />
        </Routes>
        <DevRoleSwitcher />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;